// /cliente/mis-direcciones — pantalla autoservicio para gestionar direcciones de entrega.
// Muestra:
//  - Cards por suscripción activa (editables: día/horario via modal, dirección via página existente).
//  - Sección histórico de pedidos one-shot (direcciones usadas, no editables, CTA 'usar de nuevo').
//  - Empty state si no tiene NI suscripciones NI pedidos.

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  getMisSuscripciones, getDireccionesHistoricas, cambiarDiaSuscripcion,
} from '../../lib/portalClienteApi'
import { VENTANAS_HORARIAS, labelVentana } from '../../lib/ventanasHorarias'

const DIAS_SEMANA_LABEL = {
  lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves',
  viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo',
}

const DIAS_SEMANA = [
  { v: 'lunes', l: 'Lunes' }, { v: 'martes', l: 'Martes' }, { v: 'miercoles', l: 'Miércoles' },
  { v: 'jueves', l: 'Jueves' }, { v: 'viernes', l: 'Viernes' },
]

const TIER_BADGE = {
  essential: { bg: '#e6fafc', color: '#0f7480', label: 'Essential' },
  premium:   { bg: '#fef3c7', color: '#92400e', label: 'Premium'   },
  comercial: { bg: '#ede9fe', color: '#5b21b6', label: 'Comercial' },
}

const ESTADO_PILL = {
  activa:    { color: '#16a34a', label: 'Activa' },
  pausada:   { color: '#f59e0b', label: 'Pausada' },
  cancelada: { color: '#ef4444', label: 'Cancelada' },
}

function fmtFechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: '2-digit' })
}

export default function MisDirecciones() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [suscripciones, setSuscripciones] = useState([])
  const [historicas, setHistoricas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modalEntrega, setModalEntrega] = useState(null)

  useEffect(() => {
    let cancelado = false
    if (!user?.id) return
    ;(async () => {
      const [ss, hh] = await Promise.all([
        getMisSuscripciones(user.id),
        getDireccionesHistoricas(),
      ])
      if (cancelado) return
      setSuscripciones(ss)
      setHistoricas(hh)
      setCargando(false)
    })()
    return () => { cancelado = true }
  }, [user?.id])

  const sinNada = !cargando && suscripciones.length === 0 && historicas.length === 0
  const soloHistorico = suscripciones.length === 0 && historicas.length > 0
  const titulo = soloHistorico ? 'Mis direcciones' : 'Mis kits y direcciones'
  const subtitulo = soloHistorico
    ? 'Direcciones que ya usaste en pedidos pasados.'
    : 'Cada kit tiene su dirección y horario — editás cada uno por separado.'

  function reloadData() {
    if (!user?.id) return
    Promise.all([getMisSuscripciones(user.id), getDireccionesHistoricas()])
      .then(([ss, hh]) => { setSuscripciones(ss); setHistoricas(hh) })
  }

  if (cargando) {
    return (
      <ClienteLayout title="Mis direcciones">
        <Estilos />
        <div className="md-wrap">
          <div className="md-loading">Cargando…</div>
        </div>
      </ClienteLayout>
    )
  }

  return (
    <ClienteLayout title="Mis direcciones">
      <Estilos />
      <div className="md-wrap">
        {sinNada ? (
          <EmptyState />
        ) : (
          <>
            <div className="md-headline">
              <h1>{titulo}</h1>
              <p>{subtitulo}</p>
            </div>

            {suscripciones.length > 0 && (
              <div className="md-section">
                {suscripciones.map(s => (
                  <CardSuscripcion
                    key={s.id}
                    susc={s}
                    onEditarEntrega={() => setModalEntrega(s)}
                    onCambiarDireccion={() => navigate(`/cliente/cambiar-direccion/${s.id}`)}
                  />
                ))}
                <Link to="/cliente/nueva-suscripcion" className="md-add">
                  ＋ Agregar otra suscripción
                </Link>
              </div>
            )}

            {soloHistorico && (
              <div className="md-promo">
                <div className="md-promo-eyebrow">PROBÁ LA SUSCRIPCIÓN</div>
                <div className="md-promo-h">Recibí tu kit cada mes sin pensarlo</div>
                <div className="md-promo-sub">Ahorrás 18–27% vs. el promedio del mercado. Sin permanencia.</div>
                <Link to="/kits" className="md-promo-cta">Ver los kits →</Link>
              </div>
            )}

            {historicas.length > 0 && (
              <div className="md-section">
                <h2 className="md-section-titulo">De tus pedidos pasados</h2>
                {historicas.map((h, i) => (
                  <CardHistorica key={i} hist={h} esUltima={i === 0} />
                ))}
                <p className="md-hint">
                  Para editar una dirección, suscribite a un kit o usala de nuevo en un pedido.
                </p>
              </div>
            )}
          </>
        )}

        {modalEntrega && (
          <ModalCambiarDiaHorario
            susc={modalEntrega}
            onCerrar={() => setModalEntrega(null)}
            onGuardado={() => { setModalEntrega(null); reloadData() }}
          />
        )}
      </div>
    </ClienteLayout>
  )
}

function CardSuscripcion({ susc, onEditarEntrega, onCambiarDireccion }) {
  const tier = TIER_BADGE[susc.kit?.tier] || null
  const estado = ESTADO_PILL[susc.estado] || ESTADO_PILL.activa
  const ventanaInfo = VENTANAS_HORARIAS.find(v => v.key === susc.ventana_horaria)
  const horario = ventanaInfo
    ? `${ventanaInfo.corto.replace(/^./, c => c.toUpperCase())} ${ventanaInfo.hi.slice(0,-3)}–${ventanaInfo.hf.slice(0,-3)}hs`
    : 'Horario por confirmar'
  const dia = DIAS_SEMANA_LABEL[susc.dia_entrega] || susc.dia_entrega || '—'

  return (
    <div className="md-card">
      <div className="md-card-pills">
        <span style={{ color: estado.color, fontSize: 11, fontWeight: 700 }}>● {estado.label}</span>
        {tier && <span className="md-card-tier" style={{ background: tier.bg, color: tier.color }}>{tier.label}</span>}
      </div>
      <h3 className="md-card-h">
        {susc.alias ? `🏠 ${susc.alias}` : '🏠'}
        {susc.kit?.nombre && <span style={{ opacity: 0.7 }}> · {susc.kit.nombre}</span>}
      </h3>
      <div className="md-card-meta">
        📍 <strong>{susc.direccion || '—'}</strong>{susc.barrio ? `, ${susc.barrio}` : ''}
        {susc.apto ? ` · ${susc.apto}` : ''}
        <br />
        🕐 {dia} · {horario}
      </div>
      <div className="md-card-acciones">
        <button type="button" onClick={onEditarEntrega}>✏️ Día y horario</button>
        <button type="button" onClick={onCambiarDireccion}>📍 Editar dirección</button>
      </div>
    </div>
  )
}

function CardHistorica({ hist, esUltima }) {
  const navigate = useNavigate()
  return (
    <div className="md-card md-card-hist">
      <div className="md-card-hist-head">
        <div className="md-card-hist-titulo">📍 {hist.direccion}{hist.barrio_nombre ? `, ${hist.barrio_nombre}` : ''}</div>
        {esUltima && <span className="md-pill-ultima">ÚLTIMA USADA</span>}
      </div>
      <div className="md-card-meta">
        {hist.apto && <>{hist.apto} · </>}
        Usada en {hist.veces_usada} pedido{hist.veces_usada === 1 ? '' : 's'} · Último: {fmtFechaCorta(hist.ultimo_uso)}
      </div>
      <div className="md-card-acciones">
        <button type="button" onClick={() => navigate('/catalogo')}>🛒 Usar de nuevo en la tienda</button>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="md-empty">
      <div className="md-empty-icon">📍</div>
      <h2>Todavía no tenés direcciones guardadas</h2>
      <p>Cuando te suscribís o hacés tu primer pedido, guardamos tu dirección acá automáticamente.</p>
      <div className="md-empty-ctas">
        <Link to="/kits" className="md-cta-primary">Ver los kits →</Link>
        <Link to="/catalogo" className="md-cta-secondary">Ir a la tienda →</Link>
      </div>
    </div>
  )
}

function ModalCambiarDiaHorario({ susc, onCerrar, onGuardado }) {
  const [dia, setDia] = useState(susc.dia_entrega || 'lunes')
  const [ventana, setVentana] = useState(susc.ventana_horaria || 'manana')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  const diasBarrio = (() => {
    const raw = susc.barrio_obj?.dias
    if (!raw) return null
    if (Array.isArray(raw)) return raw.map(s => String(s).toLowerCase())
    return String(raw).replace(/[{}"]/g, '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
  })()
  const diasDisponibles = diasBarrio && diasBarrio.length > 0
    ? DIAS_SEMANA.filter(d => diasBarrio.includes(d.v))
    : DIAS_SEMANA

  async function guardar() {
    setError(null)
    setGuardando(true)
    try {
      await cambiarDiaSuscripcion(susc.id, dia, ventana)
      onGuardado()
    } catch (e) {
      setError(e.message || 'No pudimos guardar. Probá de nuevo.')
      setGuardando(false)
    }
  }

  return (
    <div className="md-modal-overlay" onClick={onCerrar}>
      <div className="md-modal" onClick={e => e.stopPropagation()}>
        <header className="md-modal-head">
          <div>
            <h2>Cambiar día y horario</h2>
            <p>{susc.alias || susc.kit?.nombre || 'Suscripción'}</p>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar">✕</button>
        </header>
        <div className="md-modal-body">
          <label className="md-field">
            <span>Día {susc.barrio && <em>· días que pasamos por {susc.barrio}</em>}</span>
            <select value={dia} onChange={e => setDia(e.target.value)}>
              {diasDisponibles.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
              {dia && !diasDisponibles.some(d => d.v === dia) && (
                <option value={dia}>{DIAS_SEMANA_LABEL[dia] || dia} (día actual)</option>
              )}
            </select>
          </label>
          <div className="md-field">
            <span>Ventana horaria</span>
            <div className="md-vent-grid">
              {VENTANAS_HORARIAS.map(v => (
                <button
                  type="button"
                  key={v.key}
                  className={`md-vent ${ventana === v.key ? 'is-active' : ''}`}
                  onClick={() => setVentana(v.key)}
                >
                  <span>{v.emoji}</span>
                  <span>{v.label}</span>
                </button>
              ))}
            </div>
          </div>
          {error && <div className="md-error">{error}</div>}
        </div>
        <footer className="md-modal-foot">
          <button type="button" className="md-btn-secondary" onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button type="button" className="md-btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </footer>
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .md-wrap { max-width: 720px; margin: 0 auto; padding: 16px 16px 48px; font-family: Inter, system-ui, sans-serif; color: var(--cl-text); }
      .md-loading { padding: 40px; text-align: center; color: var(--cl-muted); }

      .md-headline { margin-bottom: 18px; }
      .md-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); margin: 0 0 4px; letter-spacing: -0.02em; }
      .md-headline p { font-size: 14px; color: var(--cl-muted); margin: 0; }

      .md-section { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 14px; padding: 14px; margin-bottom: 16px; }
      .md-section-titulo { font-size: 11px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--cl-muted); margin: 0 0 10px; padding: 0 4px; }

      .md-card { background: rgba(0,0,0,.25); border: 1px solid var(--cl-line); border-radius: 12px; padding: 14px; margin-bottom: 10px; }
      .md-card:last-child { margin-bottom: 0; }
      .md-card-pills { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
      .md-card-tier { font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 7px; border-radius: 5px; }
      .md-card-h { font-size: 16px; font-weight: 900; color: var(--cl-text); margin: 0 0 6px; }
      .md-card-meta { font-size: 13px; color: var(--cl-muted); margin-bottom: 12px; line-height: 1.55; }
      .md-card-acciones { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .md-card-acciones button { background: var(--cl-card); border: 1.5px solid var(--cl-line); padding: 9px 10px; border-radius: 10px; cursor: pointer; font-weight: 700; font-size: 13px; color: var(--cl-text); font-family: inherit; transition: all 0.15s; display: flex; align-items: center; justify-content: center; gap: 4px; }
      .md-card-acciones button:hover { border-color: var(--cl-text); }

      .md-card-hist .md-card-hist-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
      .md-card-hist-titulo { font-size: 14px; font-weight: 800; color: var(--cl-text); }
      .md-pill-ultima { background: #DCFCE7; color: #15803D; font-size: 10px; font-weight: 800; letter-spacing: 0.04em; padding: 3px 8px; border-radius: 999px; flex-shrink: 0; }
      .md-card-hist .md-card-acciones { grid-template-columns: 1fr; }

      .md-add { display: block; text-align: center; padding: 12px; border: 1.5px dashed #00A8B5; border-radius: 10px; color: #00A8B5; font-weight: 700; font-size: 13px; text-decoration: none; margin-top: 10px; transition: all 0.15s; }
      .md-add:hover { background: #00A8B5; color: #fff; border-style: solid; }

      .md-promo { background: linear-gradient(135deg, #00A8B5, #008a95); color: #fff; border-radius: 14px; padding: 16px; margin-bottom: 16px; }
      .md-promo-eyebrow { font-size: 10px; font-weight: 800; letter-spacing: 0.12em; opacity: 0.92; margin-bottom: 6px; }
      .md-promo-h { font-size: 17px; font-weight: 800; margin-bottom: 4px; letter-spacing: -0.01em; }
      .md-promo-sub { font-size: 13px; opacity: 0.92; margin-bottom: 12px; line-height: 1.5; }
      .md-promo-cta { display: inline-block; background: var(--cl-card); color: var(--cl-text); padding: 9px 14px; border-radius: 8px; font-weight: 800; font-size: 13px; text-decoration: none; }

      .md-hint { font-size: 11px; color: var(--cl-muted2, #6b7d92); text-align: center; margin: 12px 0 0; font-style: italic; }

      .md-empty { background: var(--cl-card); border-radius: 14px; padding: 40px 24px; text-align: center; border: 1px solid var(--cl-line); }
      .md-empty-icon { font-size: 48px; margin-bottom: 12px; }
      .md-empty h2 { font-size: 18px; font-weight: 800; color: var(--cl-text); margin: 0 0 8px; }
      .md-empty p { font-size: 13px; color: var(--cl-muted); margin: 0 0 18px; line-height: 1.5; }
      .md-empty-ctas { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
      .md-cta-primary { background: #00A8B5; color: #fff; padding: 10px 18px; border-radius: 10px; font-weight: 800; font-size: 13px; text-decoration: none; }
      .md-cta-secondary { background: var(--cl-card); color: var(--cl-text); padding: 10px 18px; border-radius: 10px; font-weight: 700; font-size: 13px; text-decoration: none; border: 1.5px solid var(--cl-teal); }

      .md-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
      .md-modal { background: var(--cl-card); border-radius: 16px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
      .md-modal-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 22px 12px; border-bottom: 1px solid var(--cl-line); }
      .md-modal-head h2 { font-size: 18px; font-weight: 800; margin: 0; color: var(--cl-text); }
      .md-modal-head p { font-size: 12px; color: var(--cl-muted); margin: 2px 0 0; }
      .md-modal-head button { background: none; border: none; font-size: 22px; color: var(--cl-muted); cursor: pointer; padding: 4px 10px; }
      .md-modal-body { padding: 18px 22px 4px; }
      .md-modal-foot { display: flex; gap: 10px; padding: 14px 22px 20px; border-top: 1px solid var(--cl-line); }
      .md-field { display: flex; flex-direction: column; margin-bottom: 14px; }
      .md-field > span { font-size: 12px; font-weight: 700; color: var(--cl-text); margin-bottom: 6px; }
      .md-field > span em { font-size: 11px; font-weight: 500; color: var(--cl-muted); font-style: normal; }
      .md-field select { background: rgba(0,0,0,.25); border: 1px solid var(--cl-line); border-radius: 8px; padding: 11px 12px; font-size: 14px; color: var(--cl-text); font-family: inherit; }
      .md-vent-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
      .md-vent { display: flex; align-items: center; gap: 12px; background: rgba(0,0,0,.25); border: 1.5px solid var(--cl-line); border-radius: 10px; padding: 12px 14px; cursor: pointer; font-family: inherit; transition: all 0.15s; text-align: left; }
      .md-vent:hover { border-color: #00A8B5; }
      .md-vent.is-active { border-color: #00A8B5; background: #ECFEFF; }
      .md-vent span:first-child { font-size: 22px; }
      .md-vent span:last-child { font-size: 14px; font-weight: 700; color: var(--cl-text); }
      .md-error { background: #FEE2E2; color: #991B1B; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin: 8px 0 0; }
      .md-btn-primary { flex: 1; background: #00A8B5; color: #fff; border: none; padding: 12px; border-radius: 10px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: inherit; }
      .md-btn-primary:hover:not(:disabled) { background: #008a95; }
      .md-btn-primary:disabled { opacity: 0.6; cursor: wait; }
      .md-btn-secondary { background: var(--cl-card); color: var(--cl-text); border: 1.5px solid var(--cl-line); padding: 12px 16px; border-radius: 10px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; }

      @media (max-width: 540px) {
        .md-card-acciones { grid-template-columns: 1fr; }
      }
    `}</style>
  )
}
